import { iconGuardsDisabled } from './iconGuardDiagnostics';

/**
 * VI-784 — silence the shared theme's icon-converter `replaceChild` errors.
 *
 * ## What this is (and is not)
 * This does NOT fix anything. It suppresses the console/RUM reporting of one specific, known,
 * third-party error that we have established we cannot fix from this app:
 *
 *     Uncaught NotFoundError: Failed to execute 'replaceChild' on 'Node':
 *     The node to be replaced is not a child of this node.
 *         at .../v2/iife/900.chunk.min.js
 *
 * The v2 theme's FontAwesome converter captures an element's parent, then calls
 * `parent.replaceChild(svg, i)`. Inside the platform Stencil components (`job-bar`, `change-job`,
 * `ivr-*`) Stencil re-parents that `<i>` in between, so the call throws. The throw happens between
 * two statements inside the theme bundle — there is no seam for app code, and every attempt to
 * intervene from our side either did nothing or made it worse (see reactDomNodeGuard's VI-784
 * note). The real fix is one line upstream in PA-1976: `i.replaceWith(svg)`, which resolves the
 * parent at call time.
 *
 * ## Why suppress at all, if there is no user impact
 * The errors are *uncaught*, so they reach CloudWatch RUM and drown the console: ~130-200 per
 * job selection, which buries genuine errors during debugging and pollutes RUM dashboards and
 * alerting. Suppression is about signal-to-noise, not about pretending the bug is gone.
 *
 * ## Safety — deliberately narrow
 * A match requires ALL of:
 *   - the message names `replaceChild`, AND
 *   - the message is the NotFoundError / "not a child of this node" variant, AND
 *   - the reporting script is the theme bundle host.
 * Anything else — including any other `NotFoundError`, and the same error from our own code —
 * propagates untouched. Suppressed events are COUNTED on `window.__jtSuppressedThemeIconErrors`
 * so the volume stays auditable rather than invisible, and the whole thing is switched off by
 * `?jtNoIconGuards=1` when you want to see the raw errors.
 *
 * Registered in the capture phase before CloudWatch RUM is initialised, so `stopImmediatePropagation`
 * also keeps these out of RUM.
 *
 * DELETE THIS once PA-1976 ships to our component-library / theme CDN.
 */

/** Host that serves the theme bundle whose converter throws. */
const THEME_HOST = 'uithemeplatform.jobtarget.com';

declare global {
  interface Window {
    __jtSuppressedThemeIconErrors?: number;
    __jtSuppressedBaseUiWarnings?: number;
  }
}

/**
 * The DOM-race signature: the converter calling replaceChild / insertBefore / removeChild against a
 * parent that Stencil has since relocated the node away from.
 *
 * VI-784: this deliberately no longer requires the reporting script to be the theme host. It did at
 * first, and that is exactly why nothing was ever suppressed — `reactDomNodeGuard` sits in the call
 * path, so the reporting script is OUR file, not the theme's. The guard now contains these
 * internally, so anything still reaching here is a residual of the same third-party race that we
 * have established cannot be fixed from this app.
 */
const DOM_RACE_SIGNATURE =
  /Failed to execute '(replaceChild|insertBefore|removeChild)' on 'Node'|The node (to be (replaced|removed)|before which the new node is to be inserted) is not a child of this node/;

const isConverterDomRaceError = (event: ErrorEvent): boolean => {
  const message = event.message || '';
  if (!/NotFoundError|not a child of this node/.test(message)) return false;
  if (!DOM_RACE_SIGNATURE.test(message)) return false;

  // Require the theme bundle OR our own guard in the reporting location/stack, so an unrelated
  // NotFoundError raised anywhere else in the app still surfaces normally.
  const origin = `${event.filename || ''} ${event.error?.stack || ''}`;
  return (
    origin.includes(THEME_HOST) ||
    origin.includes('reactDomNodeGuard') ||
    origin.includes('900.chunk')
  );
};

/**
 * VI-784: base-ui's own deprecation notices, emitted from inside the platform components
 * (`base-select-field.tsx:184/185`) — the Division / Job Status selects in the change-job picker
 * alone produce ~300 per page. We do not pass these props: `base-select-field`, `onSelected` and
 * `selectedField` appear nowhere in this app, so there is nothing for us to migrate; the component
 * is warning about its own internal API usage. Filtered so real warnings stay visible.
 *
 * Matched on the base-ui replacement API name so it cannot swallow one of our own warnings.
 */
const BASE_UI_DEPRECATION =
  /is deprecated\.\s*Please use\s+(onBaseSelectField|baseSelectField)|is deprecated and will be removed in v\d+ of the component library/i;

const installBaseUiDeprecationFilter = (): void => {
  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]): void => {
    const first = typeof args[0] === 'string' ? args[0] : '';
    if (BASE_UI_DEPRECATION.test(first)) {
      window.__jtSuppressedBaseUiWarnings = (window.__jtSuppressedBaseUiWarnings ?? 0) + 1;
      return;
    }
    originalWarn(...args);
  };
};

export function installThemeIconErrorSuppressor(): void {
  if (typeof window === 'undefined') return;
  if (iconGuardsDisabled()) return;

  window.__jtSuppressedThemeIconErrors = 0;
  window.__jtSuppressedBaseUiWarnings = 0;
  installBaseUiDeprecationFilter();

  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (!isConverterDomRaceError(event)) return;

      window.__jtSuppressedThemeIconErrors = (window.__jtSuppressedThemeIconErrors ?? 0) + 1;

      // preventDefault stops Chrome logging it as "Uncaught …"; stopImmediatePropagation keeps it
      // away from listeners registered later, notably CloudWatch RUM's.
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true // capture — run before bubble-phase listeners such as RUM's
  );
}
